import React, { useState, useRef, useEffect } from 'react';
import { normalizeText } from './normalizeText';

/**
 * Accent/case-insensitive city autocomplete. Typing "Evora" suggests "Évora".
 * Suggestions come only from the counties stored in the DB, so users can only
 * pick real locations.
 */
export default function CityAutocomplete({
  value, onChange, placeholder, counties, inputClassName, dropdownClassName,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const key = normalizeText(value);

  const filtered = key
    ? (counties || [])
        .filter((c) => normalizeText(c.name).includes(key))
        .sort((a, b) => {
          // Concelhos (no parent_concelho) always appear before freguesias
          const aIsConcelho = !a.parent_concelho;
          const bIsConcelho = !b.parent_concelho;
          if (aIsConcelho && !bIsConcelho) return -1;
          if (!aIsConcelho && bIsConcelho) return 1;
          // Exact match first, then starts-with, then includes
          const aName = normalizeText(a.name);
          const bName = normalizeText(b.name);
          if (aName === key && bName !== key) return -1;
          if (bName === key && aName !== key) return 1;
          if (aName.startsWith(key) && !bName.startsWith(key)) return -1;
          if (bName.startsWith(key) && !aName.startsWith(key)) return 1;
          return (a.name || '').localeCompare(b.name || '');
        })
        .slice(0, 60)
    : [];

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full">
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
        className={inputClassName}
      />
      {open && filtered.length > 0 && (
        <div className={`absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto ${dropdownClassName || ''}`}>
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onChange(c.name); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                normalizeText(c.name) === key ? 'bg-[var(--brand)]/10 text-[var(--brand)] font-semibold' : 'hover:bg-gray-50 text-gray-700'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}